import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { FlexColumn } from '@elementary/common-ui/components';
import useIncidentOverviewSidePanel from 'incidents/hooks/useIncidentOverviewSidePanel';
import { space } from '@elementary/common-ui/style/utils';
import useIncidentMetadata from '../../hooks/useIncidentMetadata';
import type { IncidentDetail, Monitor } from '../../types';
import { groupConsecutiveEvents } from '../utils';
import TimelineEventGroup from './TimelineEventGroup';

const Container = styled(FlexColumn)`
  gap: 0;
  padding: ${space('lg')};
`;

const EmptyState = styled(Typography)`
  color: ${({ theme }) => theme.palette.content.secondary};
  text-align: center;
  padding: ${space('xxl')};
`;

type Props = {
  incident: IncidentDetail;
};

const IncidentTimelineTab = ({ incident }: Props) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const { usersData } = useIncidentMetadata();
  const { onOpen } = useIncidentOverviewSidePanel();

  const sortedEvents = [...incident.events].sort(
    (a, b) => new Date(b.eventTime).getTime() - new Date(a.eventTime).getTime(),
  );

  const monitorsBySignalId = useMemo(
    () =>
      Object.fromEntries(
        incident.monitors
          .filter((m): m is Monitor & { signal: { id: string } } =>
            Boolean(m.signal?.id),
          )
          .map((m) => [m.signal.id, m]),
      ),
    [incident.monitors],
  );

  const eventGroups = groupConsecutiveEvents(sortedEvents, usersData, {
    onOpenIncident: onOpen,
    monitorsBySignalId,
  });

  const toggleGroup = (groupIndex: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupIndex)) {
        next.delete(groupIndex);
      } else {
        next.add(groupIndex);
      }
      return next;
    });
  };

  if (sortedEvents.length === 0) {
    return (
      <Container>
        <EmptyState>No events yet</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      {eventGroups.map((group, groupIndex) => (
        <TimelineEventGroup
          key={group.firstEvent.id}
          group={group}
          isExpanded={expandedGroups.has(groupIndex)}
          isLastGroup={groupIndex === eventGroups.length - 1}
          onToggle={() => toggleGroup(groupIndex)}
          usersData={usersData}
          onOpenIncident={onOpen}
        />
      ))}
    </Container>
  );
};

export default IncidentTimelineTab;
